import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import PartnerNavbar from './components/PartnerNavbar';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Agreement from './pages/Agreement';
import LicensingSettings from './pages/LicensingSettings';
import PartnerLogin from './pages/PartnerLogin';
import PartnerRegister from './pages/PartnerRegister';
import PartnerDashboard from './pages/PartnerDashboard';
import PartnerInventory from './pages/PartnerInventory';
import AdminLogin from './pages/AdminLogin';
import AdminLayout from './components/AdminLayout';
import AdminDashboard from './pages/AdminDashboard';
import AdminRates from './pages/AdminRates';
import AdminScheduling from './pages/AdminScheduling';
import AdminDisplayOrders from './pages/AdminDisplayOrders';
import AdminActivity from './pages/AdminActivity';
import AdminDoubleShoot from './pages/AdminDoubleShoot';
import AdminBilling from './pages/AdminBilling';
import AdminPhotographers from './pages/AdminPhotographers';
import AdminUsers from './pages/AdminUsers';
import AdminMobile from './pages/AdminMobile';
import AdminDealers from './pages/AdminDealers';
import AdminFeeds from './pages/AdminFeeds';
import AdminFileUpload from './pages/AdminFileUpload';
import AdminReports from './pages/AdminReports';
import AdminManualReview from './pages/AdminManualReview';
import AdminReminders from './pages/AdminReminders';
import AdminHealth from './pages/AdminHealth';
import PhotographerLogin from './pages/PhotographerLogin';
import PhotographerInventory from './pages/PhotographerInventory';
import PhotographerVehicle from './pages/PhotographerVehicle';
import Setup from './pages/Setup';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

function homeFor(userType) {
  if (userType === 'partner') return '/partner';
  if (userType === 'photographer') return '/photographer';
  return '/';
}

function PrivateRoute({ children, requiredType }) {
  const { user, userType, loading } = useAuth();
  if (loading) return <div className="loading">Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  if (requiredType && userType !== requiredType) {
    return <Navigate to={homeFor(userType)} />;
  }
  return children;
}

function AdminRoute({ children }) {
  const isAdmin = localStorage.getItem('adminAuth');
  if (!isAdmin) return <Navigate to="/admin/login" />;
  return children;
}

function AppRoutes() {
  const { user, userType } = useAuth();

  return (
    <BrowserRouter>
      {user && userType === 'dealer' && <Navbar />}
      {user && userType === 'partner' && <PartnerNavbar />}
      <div className={user ? 'main-content' : ''}>
        <Routes>
          {/* Public routes */}
          <Route path="/setup/:token" element={<Setup />} />

          {/* Dealer routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/" element={<PrivateRoute requiredType="dealer"><Dashboard /></PrivateRoute>} />
          <Route path="/profile" element={<PrivateRoute requiredType="dealer"><Profile /></PrivateRoute>} />
          <Route path="/agreement" element={<PrivateRoute requiredType="dealer"><Agreement /></PrivateRoute>} />
          <Route path="/licensing" element={<PrivateRoute requiredType="dealer"><LicensingSettings /></PrivateRoute>} />

          {/* Partner routes */}
          <Route path="/partner/login" element={<PartnerLogin />} />
          <Route path="/partner/register" element={<PartnerRegister />} />
          <Route path="/partner" element={<PrivateRoute requiredType="partner"><PartnerDashboard /></PrivateRoute>} />
          <Route path="/partner/inventory/:dealerId" element={<PrivateRoute requiredType="partner"><PartnerInventory /></PrivateRoute>} />

          {/* Photographer routes */}
          <Route path="/photographer/login" element={<PhotographerLogin />} />
          <Route path="/photographer" element={<PrivateRoute requiredType="photographer"><PhotographerInventory /></PrivateRoute>} />
          <Route path="/photographer/vehicle/:dealerId/:vin" element={<PrivateRoute requiredType="photographer"><PhotographerVehicle /></PrivateRoute>} />

          {/* Admin routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/rates" element={<AdminRates />} />
            <Route path="/admin/scheduling" element={<AdminScheduling />} />
            <Route path="/admin/display-orders" element={<AdminDisplayOrders />} />
            <Route path="/admin/activity" element={<AdminActivity />} />
            <Route path="/admin/double-shoot" element={<AdminDoubleShoot />} />
            <Route path="/admin/billing" element={<AdminBilling />} />
            <Route path="/admin/photographers" element={<AdminPhotographers />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/mobile" element={<AdminMobile />} />
            <Route path="/admin/dealers" element={<AdminDealers />} />
            <Route path="/admin/feeds" element={<AdminFeeds />} />
            <Route path="/admin/file-upload" element={<AdminFileUpload />} />
            <Route path="/admin/reports" element={<AdminReports />} />
            <Route path="/admin/manual-review" element={<AdminManualReview />} />
            <Route path="/admin/reminders" element={<AdminReminders />} />
            <Route path="/admin/health" element={<AdminHealth />} />
          </Route>
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ErrorBoundary>
  );
}
